/**
 * Risk-assessment draft persistence (Fredrik, 2026-08-21).
 *
 * "Complete Review" can be refused for reasons the reviewer only discovers on
 * submit — an embedded child not yet marked reviewed being the common one. The
 * fix for that is somewhere else on the page, so the reviewer navigates away,
 * does it, and comes back. Until now the summary they had written was gone:
 * the form state lives in ReviewInterface, so anything that remounts it (or a
 * refetch that re-applies server values over it) took the text with it. On a
 * long summary that is real lost work, and it is lost silently.
 *
 * sessionStorage, not localStorage: a draft is scoped to the tab and the
 * sitting, and should not outlive the browser session on a shared machine.
 * Keyed by cycle AND reviewer so two people on one machine cannot inherit each
 * other's words, and so drafts on different cycles never collide.
 *
 * Every operation is wrapped: storage can throw (private mode, quota, disabled
 * cookies) and a draft is a convenience — it must never break the form.
 */

export interface AssessmentDraft {
  riskLevel: string;
  summary: string;
}

const PREFIX = 'cellcohub:assessmentDraft';

export function draftKey(cycleId: string, email?: string | null): string {
  return `${PREFIX}:${cycleId}:${(email || 'anon').toLowerCase()}`;
}

export function readDraft(cycleId: string, email?: string | null): AssessmentDraft | null {
  try {
    const raw = window.sessionStorage.getItem(draftKey(cycleId, email));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const riskLevel = typeof parsed.riskLevel === 'string' ? parsed.riskLevel : '';
    const summary = typeof parsed.summary === 'string' ? parsed.summary : '';
    if (!riskLevel && !summary) return null;
    return { riskLevel, summary };
  } catch {
    return null;
  }
}

export function writeDraft(cycleId: string, email: string | null | undefined, draft: AssessmentDraft): void {
  try {
    // An empty draft is not worth a key — and writing one would resurrect an
    // empty form over a server value on the next read.
    if (!draft.riskLevel && !draft.summary.trim()) {
      window.sessionStorage.removeItem(draftKey(cycleId, email));
      return;
    }
    window.sessionStorage.setItem(draftKey(cycleId, email), JSON.stringify(draft));
  } catch {
    /* storage unavailable — the form still works, the draft just isn't kept */
  }
}

export function clearDraft(cycleId: string, email?: string | null): void {
  try {
    window.sessionStorage.removeItem(draftKey(cycleId, email));
  } catch {
    /* nothing to do */
  }
}
